import type { DocPageContent } from '../types';

/**
 * "Compliance reports" — Dashboard section (ADR-0074).
 *
 * The evidence a customer hands to somebody else — a compliance officer, a
 * security reviewer, a procurement questionnaire.
 *
 * What is documented here is the always-on C-21 SCAN, which is unchanged: it
 * still scores every conversation, still routes its `COMPLIANCE_VIOLATION`
 * webhook, and its findings still surface on `/users/:sub`. The detector list
 * is described by what it looks for, never by how it decides — a published
 * detection rule is a published evasion guide.
 *
 * Two DASHBOARD surfaces this page also covered have since been removed: the
 * per-tenant red-team report (ADR-0532) and the `/compliance` operator page
 * (ADR-0536). Their sections went with them — the red-team one because the
 * per-workspace deliverable is gone (its adversarial corpus still gates every
 * release and is described on the public security page), the scan-settings one
 * because the two controls it described are no longer reachable from a page.
 */
export const DASHBOARD_COMPLIANCE_PAGE: DocPageContent = {
  slug: 'dashboard/compliance',
  titleKey: 'docs.dashboard.compliance.title',
  descriptionKey: 'docs.dashboard.compliance.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.compliance.lead' },

    { kind: 'heading', level: 2, id: 'scan', textKey: 'docs.dashboard.compliance.scan.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.compliance.scan.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.compliance.scan.item.advice',
        'docs.dashboard.compliance.scan.item.pii',
        'docs.dashboard.compliance.scan.item.fraud',
        'docs.dashboard.compliance.scan.item.guarantee',
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.dashboard.compliance.scan.derived.title',
      textKey: 'docs.dashboard.compliance.scan.derived.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.compliance.next.security.title',
          textKey: 'docs.dashboard.compliance.next.security.text',
          to: { docSlug: 'trust/security' },
        },
        {
          titleKey: 'docs.dashboard.compliance.next.compliance.title',
          textKey: 'docs.dashboard.compliance.next.compliance.text',
          to: { docSlug: 'trust/compliance' },
        },
        {
          titleKey: 'docs.dashboard.compliance.next.audit.title',
          textKey: 'docs.dashboard.compliance.next.audit.text',
          to: { docSlug: 'dashboard/audit' },
        },
      ],
    },
  ],
};
